import useAppModal from 'hooks/useAppModal';
import { ModalBody } from 'react-bootstrap';
import FormWithProvider from 'components/forms/form/Form';
import FormFieldset from 'components/forms/form/FormFieldset';
import { useQueryClient } from '@tanstack/react-query';
import { GUEST_REVIEW_TEMPLATES_BASE_QUERY_KEY } from 'components/domain/review/useFetchGuestReviewTemplates';
import { AUTOMATED_REVIEW_LOGS_BASE_QUERY_KEY } from 'components/domain/review/automatedReviewLogs/automatedReviewLogsQuery';
import { AdditionalManualReviewPayload } from 'models/automation/Reviews';
import useFetchGuestReviewTemplate from 'components/domain/review/useFetchGuestReviewTemplate';
import {
  ReviewTemplateSchemaFormValues,
  getFormDefaultValues,
  reviewTemplateSchema,
  reviewTemplateSchemaVrbo,
} from './AddEditReviewTemplateModal.schema';
import ReviewTemplateModalContent from './ReviewTemplateModalContent';
import ReviewTemplateModalFooter from './ReviewTemplateModalFooter';
import useReviewTemplateFormSubmit from './useReviewTemplateFormSubmit';

const AddEditReviewTemplateModal = ({
  modalId,
  reviewTemplateUid,
  manualReviewPayload = undefined,
  isVrbo = false,
}: {
  modalId: string;
  reviewTemplateUid?: string;
  manualReviewPayload?: AdditionalManualReviewPayload;
  isVrbo?: boolean;
}) => {
  const { data: reviewTemplate } = useFetchGuestReviewTemplate({
    reviewTemplateUid,
    enabled: !!reviewTemplateUid,
  });
  const { closeModal } = useAppModal();
  const { handleSubmit } = useReviewTemplateFormSubmit();
  const queryClient = useQueryClient();

  const isManualReviewMode = manualReviewPayload !== undefined;

  const onSubmit = async (values: ReviewTemplateSchemaFormValues) => {
    await handleSubmit({ values, reviewTemplate, manualReviewPayload });

    closeModal(modalId);

    if (isManualReviewMode) {
      queryClient.invalidateQueries({
        queryKey: [AUTOMATED_REVIEW_LOGS_BASE_QUERY_KEY, 'list'],
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: [GUEST_REVIEW_TEMPLATES_BASE_QUERY_KEY, 'get-all'],
      });
    }
  };

  return (
    <FormWithProvider
      key={JSON.stringify(reviewTemplate)}
      horizontal
      defaultValues={getFormDefaultValues(reviewTemplate, isVrbo)}
      schema={isVrbo ? reviewTemplateSchemaVrbo() : reviewTemplateSchema()}
      onSubmit={onSubmit}
    >
      <FormFieldset>
        <ModalBody>
          <ReviewTemplateModalContent
            hideFieldsForVrbo={isVrbo && isManualReviewMode}
          />
        </ModalBody>
        <ReviewTemplateModalFooter isManualReviewMode={isManualReviewMode} />
      </FormFieldset>
    </FormWithProvider>
  );
};

AddEditReviewTemplateModal.defaultProps = {
  reviewTemplateUid: undefined,
  manualReviewPayload: undefined,
  isVrbo: false,
};

export default AddEditReviewTemplateModal;
